import React, { useCallback } from 'react';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import Grid from '@mui/material/Grid2';
import Autocomplete from '@mui/material/Autocomplete';
import Add from '@mui/icons-material/Add';
import Delete from '@mui/icons-material/Delete';
import { TemplateTypeEnum, LogsTemplateField } from '@/__generated__/graphql';

export interface Variable {
  fieldKey: string;
  name: string;
  templateType: TemplateTypeEnum;
  isBlisterField?: boolean;
}

interface VariableEditorProps {
  variables: Variable[];
  availableFieldKeys: string[];
  availableFields: LogsTemplateField[];
  mode: 'ReadOnly' | 'Edit';
  onVariablesChange: (variables: Variable[]) => void;
  onCreateNewField?: (fieldKey: string, equationKey: string) => void;
  equationKey: string;
}

export const VariableEditor: React.FC<VariableEditorProps> = ({
  variables,
  availableFieldKeys,
  availableFields,
  mode,
  onVariablesChange,
  onCreateNewField,
  equationKey,
}) => {
  const getFieldNameByKey = useCallback(
    (fieldKey: string) => {
      const field = availableFields.find((f) => f.key === fieldKey);
      return field?.name ?? fieldKey;
    },
    [availableFields]
  );

  const addVariable = useCallback(() => {
    const newVariable: Variable = {
      fieldKey: availableFieldKeys[0] ?? '',
      name: 'New Variable',
      templateType: TemplateTypeEnum.Workpack,
    };
    onVariablesChange([...variables, newVariable]);
  }, [variables, availableFieldKeys, onVariablesChange]);

  const updateVariable = useCallback(
    (index: number, updates: Partial<Variable>) => {
      const updatedVariables = variables.map((variable, i) =>
        i === index ? { ...variable, ...updates } : variable
      );
      onVariablesChange(updatedVariables);
    },
    [variables, onVariablesChange]
  );

  const deleteVariable = useCallback(
    (index: number) => {
      const updatedVariables = variables.filter((_, i) => i !== index);
      onVariablesChange(updatedVariables);
    },
    [variables, onVariablesChange]
  );

  return (
    <Stack spacing={2}>
      {mode === 'Edit' && (
        <Button
          startIcon={<Add />}
          variant="outlined"
          size="small"
          onClick={addVariable}
          sx={{ alignSelf: 'flex-start' }}
        >
          Add Variable
        </Button>
      )}

      {variables.length > 0 ? (
        <Stack spacing={2}>
          {variables.map((variable, varIndex) => (
            <Card key={`var-${varIndex}`} variant="outlined" sx={{ bgcolor: 'grey.50' }}>
              <CardContent>
                <Grid container spacing={2} alignItems="flex-start">
                  <Grid size={{ xs: 12, md: 6 }}>
                    <TextField
                      fullWidth
                      label="Variable Name"
                      value={variable.name ?? ''}
                      onChange={(e) =>
                        updateVariable(varIndex, {
                          name: e.target.value,
                        })
                      }
                      disabled={mode === 'ReadOnly'}
                      size="small"
                    />
                  </Grid>

                  <Grid size={{ xs: 12, md: 5 }}>
                    <Autocomplete
                      fullWidth
                      size="small"
                      disabled={mode === 'ReadOnly'}
                      freeSolo
                      value={variable.fieldKey ?? ''}
                      options={availableFieldKeys}
                      onChange={(_, newValue) => {
                        if (newValue) {
                          const fieldKey = String(newValue);
                          const fieldName = getFieldNameByKey(fieldKey);

                          updateVariable(varIndex, {
                            fieldKey,
                            name: fieldName,
                          });

                          if (!availableFieldKeys.includes(fieldKey) && onCreateNewField) {
                            onCreateNewField(fieldKey, equationKey);
                          }
                        }
                      }}
                      onInputChange={(_, newInputValue) => {
                        const fieldName = getFieldNameByKey(newInputValue);
                        updateVariable(varIndex, {
                          fieldKey: newInputValue,
                          name: fieldName,
                        });
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Field Key"
                          helperText={
                            variable.fieldKey && !availableFieldKeys.includes(variable.fieldKey)
                              ? 'New field will be created automatically'
                              : 'Select existing or type new field key'
                          }
                        />
                      )}
                    />
                  </Grid>

                  <Grid size={{ xs: 12, md: 1 }}>
                    {mode === 'Edit' && (
                      <IconButton
                        onClick={() => deleteVariable(varIndex)}
                        color="error"
                        size="small"
                      >
                        <Delete />
                      </IconButton>
                    )}
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          ))}
        </Stack>
      ) : (
        <Typography color="text.secondary" textAlign="center">
          No variables configured. {mode === 'Edit' && 'Click "Add Variable" to create one.'}
        </Typography>
      )}
    </Stack>
  );
};
